import { ArrowDown, ArrowRight, BadgeCheck, Banknote, Brain, Camera, Check, ChevronDown, ChevronUp, Coffee, Expand, Eye, FileText, Gem, Gift, MapPin, MessageCircle, Minimize, PenLine, Play, QrCode, Search, Settings2, ShieldCheck, Sparkles, Star, Store, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { apiRequest } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { PublicFooter, PublicHeader, usePublicLocale } from '../../layouts/PublicFrame';
import { loadLandingData } from '../landing/landingApi';
import { NearbyMap } from '../landing/NearbyMap';
import { storeConfig, type LandingPlace, type MerchantCampaignPackage } from '../landing/types';

const slideTitles = [
  'ทำให้ร้านเด่นตอนลูกค้ากำลังเลือก',
  'ร้านเปิด Campaign แล้วระบบทำงานอย่างไร',
  'ยืนยันว่ามาร้านจริงก่อนเผยแพร่รีวิว',
  'เปลี่ยนเสียงรีวิวเป็น Insight ของร้าน',
  'เทียบผลตอบแทนจาก Package จริง',
  'เริ่ม Campaign แรกกับลูกค้ากลุ่มแรก',
];

const slideTags = [
  'แผนที่ใกล้ตัวพร้อม Reward ที่ช่วยตัดสินใจ',
  'ร้านเปิดแคมเปญ → ลูกค้ามาลอง → ร้านได้คุณค่า',
  'พิกัด รูปถ่าย และการตรวจสอบหลายชั้น · รองรับหลักฐานจาก Rider',
  'หัวข้อที่คนพูดถึง เสียงจริง และ Positioning',
  'จำนวนลูกค้า รีวิว รายได้ประมาณการ และแต้ม',
  'แพ็กเกจเริ่มต้นจากข้อมูลจริงของระบบ',
];
const defaultPresentationStoreName = 'Tri-Ton Cafe';
const presentationStoreNameKey = 'beaw.presentation.storeName';

const slideEyebrows = [
  'ให้เราเป็นตัวเลือกแรก / BE THE FIRST CHOICE',
  'วิธีทำงาน / HOW BEAW WORKS',
  'เชื่อถือได้ / TRUSTWORTHY',
  'COMMUNITY INSIGHT / WHAT THE STORE LEARNS',
  'ประเมินผลตอบแทน / CAMPAIGN ROI',
  'เริ่มเลย',
];

const assumedSpendOptions = [
  { amount: 65, label: 'กาแฟ' },
  { amount: 85, label: 'กาแฟ + เบเกอรี่' },
  { amount: 200, label: 'มื้ออาหาร' },
] as const;
const minimumAverageSpend = 50;
const maximumAverageSpend = 500;
const defaultAverageSpend = 65;

function spendPosition(amount: number) {
  return ((amount - minimumAverageSpend) / (maximumAverageSpend - minimumAverageSpend)) * 100;
}

const packageObjectives: Record<string, string> = {
  trial_50: 'อยากลองก่อน', boost_100: 'อยากทำต่อเนื่อง', growth_150: 'อยากเร่งการเข้าร่วม',
  volume_250: 'อยากได้จำนวน', premium_100: 'อยากเพิ่มแรงจูงใจ',
};

const pitchMapFallbackPlaces: LandingPlace[] = [
  { place_id: 'your-cafe', name: defaultPresentationStoreName, lat: null, lng: null, distance_m: 450, category: 'คาเฟ่', image_url: '', rating: null, review_count: 0, rank: 0, selected: true, has_campaign: true, campaign: { campaign_id: 'your-campaign', reward_points: 7, remaining_slots: 50 } },
  { place_id: 'graph', name: 'Graph', lat: null, lng: null, distance_m: 320, category: 'คาเฟ่', image_url: '', rating: 4.8, review_count: 24, rank: 1, selected: false, has_campaign: false, campaign: null },
  { place_id: 'foei', name: 'FOEI', lat: null, lng: null, distance_m: 510, category: 'คาเฟ่', image_url: '', rating: 4.7, review_count: 18, rank: 2, selected: false, has_campaign: false, campaign: null },
  { place_id: 'local-restaurant', name: 'Local Restaurant', lat: null, lng: null, distance_m: 680, category: 'ร้านอาหาร', image_url: '', rating: 4.6, review_count: 31, rank: 3, selected: false, has_campaign: false, campaign: null },
  { place_id: 'campaign-b', name: 'Cafe B', lat: null, lng: null, distance_m: 740, category: 'คาเฟ่', image_url: '', rating: 4.5, review_count: 12, rank: 4, selected: false, has_campaign: true, campaign: { campaign_id: 'campaign-b', reward_points: 12, remaining_slots: 20 } },
];

function loadPresentationStoreName() {
  try {
    return localStorage.getItem(presentationStoreNameKey)?.trim() || defaultPresentationStoreName;
  } catch {
    return defaultPresentationStoreName;
  }
}

function pitchMapLocation() {
  try {
    const stored = JSON.parse(localStorage.getItem('beaw.ai.location') || '{}') as Record<string, unknown>;
    const location = (stored.user_location || stored.userLocation || {}) as Record<string, unknown>;
    const lat = Number(location.lat); const lng = Number(location.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { city: String(stored.city || storeConfig.defaultCity), coords: { lat, lng } };
  } catch { /* Use the city-center presentation fallback. */ }
  return { city: storeConfig.defaultCity, coords: { lat: 18.7883, lng: 98.9853 } };
}

const presentationMarkdown = `# Beaw.club — Merchant Customer Pitch: {{STORE_NAME}}

## 1. พาคนมาลองร้านคุณ
จากคนที่กำลังตัดสินใจ → สู่คนที่เข้าร้านและทดลองใช้จริง

**Beaw เพิ่ม “เหตุผลให้ไปลอง” ในจังหวะที่ลูกค้ากำลังเลือกร้าน**

มาร้านจริง · ทดลองจริง · รีวิวจริง

## 2. Beaw ทำงานอย่างไร ร้านได้อะไร
ร้านปักหมุด เติมแต้ม และเปิด Campaign เพื่อเพิ่มเหตุผลให้คนมาลอง

ลูกค้าเปิด Beaw → ค้นพบร้านใกล้ตัว → เห็นข้อมูลร้าน รีวิวจาก Community และสิทธิพิเศษ → ตัดสินใจง่ายขึ้น → มาที่ร้าน → ทดลองใช้บริการ → Check-in → รีวิว → รับแต้ม

ร้านได้ลูกค้าที่เข้ามาทดลอง โอกาสสร้างรายได้ รีวิวจากประสบการณ์จริง และข้อมูลว่า Community พูดอะไรเกี่ยวกับร้าน

## 3. ไปจริง รีวิวจริง ถึงได้ Reward
อยู่ในรัศมีร้าน 50–100 เมตร → On-site Check-in → รีวิวพร้อมรูปถ่ายจากประสบการณ์จริง → รีวิวผ่านการตรวจสอบ → รับ Reward

กรณีใช้บริการผ่าน Rider สามารถแนบหลักฐาน เช่น บิลหรือใบเสร็จ เพื่อยืนยันว่าใช้บริการจริง โดยหลักฐานใช้ประกอบการตรวจสอบและไม่แสดงในโพสต์

3 เสาหลักของความน่าเชื่อถือ
- ใช้บริการจริง: ยืนยันด้วยพิกัดหรือหลักฐาน
- รีวิวอิสระ: จากประสบการณ์จริง
- ตรวจสอบได้: ระบบตรวจสอบหลายชั้น

ตรวจสอบการมาร้านได้ สิ่งที่ร้านซื้อคือแรงจูงใจให้คนมาลองจริง

## 4. ร้านไม่ได้แค่จำนวนรีวิว
แต่ได้รู้ว่า “คนพูดถึงร้านคุณว่าอะไร”

- What happened: รีวิวผ่านเกณฑ์ 128 รีวิว · 98 Creators · 12,450 Views
- What people mention: ลาเต้ · ไม่หวาน · นั่งทำงาน · คั่วกลาง
- What people actually say: “ลาเต้ไม่หวาน คั่วกลางกำลังดี” · “ร้านเงียบ นั่งทำงานได้นาน” · “มีเมล็ดกาแฟให้เลือกหลายแบบ”
- What it means: ลูกค้าจำร้านคุณว่า “ร้านกาแฟที่ลาเต้ไม่หวาน นั่งทำงานได้ และมีเมล็ดให้เลือก”

Insight นี้นำไปใช้ต่อได้กับ Menu · Content · Positioning · Campaign · Service

## 5. ทุกวันมีคนกำลังเลือกร้าน
“วันนี้กินอะไร?” “จะไปร้านไหนดี?” “มีอะไรน่าลองใกล้ ๆ?”

ร้านทุกแห่งถูกค้นพบได้ ส่วน Campaign เพิ่มเหตุผลให้ลูกค้าไปลอง — เหมือน Quest บนแผนที่

ถ้าลูกค้ากำลังเลือกระหว่างร้านใกล้ตัว ร้านคุณจะเป็นเพียงหนึ่งจุด หรือเป็นร้านที่มีเหตุผลให้ไปลองวันนี้?

## 6. ลงทุนกับ Campaign แล้วได้อะไรกลับมา?
สมมติยอดใช้จ่ายของลูกค้า {{AVERAGE_SPEND}} บาทต่อคน แล้วคำนวณจากราคาและจำนวนรีวิวสูงสุดของแพ็กเกจที่เปิดขายจริง

| เป้าหมาย | Package | ราคา | ลูกค้า/รีวิวสูงสุด | ยอดขายประมาณการ | ลงทุน 1 บาท | ผลลัพธ์ตามราคาจริง |
|---|---|---:|---:|---:|---:|---:|
{{PACKAGE_ROWS}}

ยอดขายประมาณการ = ลูกค้า/รีวิวสูงสุด × {{AVERAGE_SPEND}} บาท ตัวเลขนี้ใช้เพื่อเปรียบเทียบแพ็กเกจ ไม่ใช่การรับประกันผลตอบแทน

## 7. ทุกวันมีคนกำลังตัดสินใจว่าจะไปร้านไหน

ทำให้ร้านคุณเป็นหนึ่งในตัวเลือกที่ใช่ ด้วยแรงจูงใจเล็ก ๆ ที่เปลี่ยนใจให้เขา “ออกไปลอง” วันนี้

- เข้าถึงลูกค้าใหม่ที่กำลังมองหา
- พาเข้าร้านจริง เกิดประสบการณ์จริง
- มีโอกาสซื้อซ้ำและบอกต่อ

{{ENTRY_OFFER}}
`;

function packageEconomics(value: MerchantCampaignPackage, averageSpend: number) {
  const investment = Math.max(0, Number(value.price_amount) || 0);
  const reviewSlots = Math.max(0, Number(value.review_slots) || 0);
  const rewardPointsPerReview = Math.max(0, Number(value.creator_reward_per_review) || 0);
  const estimatedRevenue = reviewSlots * averageSpend;
  const revenueMultiple = investment > 0 ? estimatedRevenue / investment : 0;
  return {
    investment,
    reviewSlots,
    rewardPointsPerReview,
    estimatedRevenue,
    revenueMultiple,
  };
}

function creatorRewardLevel(rewardPointsPerReview: number, benchmark: number) {
  if (!rewardPointsPerReview || !benchmark) return { label: 'Reward มาตรฐาน', className: 'is-neutral' };
  if (rewardPointsPerReview >= benchmark * 1.1) return { label: 'Reward สูง', className: 'is-high' };
  if (rewardPointsPerReview <= benchmark * .9) return { label: 'Reward เริ่มต้น', className: 'is-low' };
  return { label: 'Reward มาตรฐาน', className: 'is-neutral' };
}

function presentationPackageName(value: MerchantCampaignPackage) {
  const name = value.name?.th || value.name?.en || value.package_id;
  return name.replace(/\s+\d+\s*$/, '').trim() || name;
}

function CampaignRoiCard({ value, averageSpend, creatorRewardBenchmark }: { value: MerchantCampaignPackage; averageSpend: number; creatorRewardBenchmark: number }) {
  const economics = packageEconomics(value, averageSpend);
  const competitiveness = creatorRewardLevel(economics.rewardPointsPerReview, creatorRewardBenchmark);
  const packageName = presentationPackageName(value);
  return <article className="pitch-roi-card">
    <header><div><small>{packageObjectives[value.package_id] || 'แพ็กเกจ Campaign'}</small><h3>{packageName}</h3></div><strong>{economics.investment.toLocaleString('th-TH')} <small>บาท</small></strong></header>
    <div className={`pitch-roi-card__points ${competitiveness.className}`}><Gem /><span><b>+{economics.rewardPointsPerReview.toLocaleString('th-TH')} บาท / รีวิว</b></span><strong>{competitiveness.label}</strong></div>
    <dl>
      <div><dt>ประมาณจำนวนลูกค้า, รีวิว</dt><dd>{economics.reviewSlots.toLocaleString('th-TH')} คน</dd></div>
      <div><dt>สมมติยอดใช้จ่ายต่อคน</dt><dd>{averageSpend.toLocaleString('th-TH')} บาท</dd></div>
      <div className="is-revenue"><dt>ยอดขายประมาณการ</dt><dd>{economics.estimatedRevenue.toLocaleString('th-TH')} บาท</dd></div>
    </dl>
    <div className="pitch-roi-card__result"><span><small>ลงทุน 1 บาท</small><b>สร้างยอดขายได้ {economics.revenueMultiple.toLocaleString('th-TH', { maximumFractionDigits: 2 })}x</b></span><span><small>ลงทุนจริง {economics.investment.toLocaleString('th-TH')} บาท</small><b>ยอดขายประมาณการ {economics.estimatedRevenue.toLocaleString('th-TH')} บาท</b></span></div>
  </article>;
}

function Flow({ items }: { items: string[] }) {
  return <div className="pitch-flow">{items.map((item, index) => <div key={item}><span>{item}</span>{index < items.length - 1 ? <ArrowDown aria-hidden="true" /> : null}</div>)}</div>;
}

function QuestMap({ places, city, compact = false, hero }: { places: LandingPlace[]; city: string; compact?: boolean; hero?: { label: string; points: number } }) {
  return <div className={`pitch-map-frame${compact ? ' is-compact' : ''}${hero ? ' has-pitch-hero' : ''}`}><NearbyMap places={places} city={city} interactive={false}/>{hero && <div className="pitch-map-hero" aria-label={`${hero.label} รับ ${hero.points} แต้ม`}><span className="pitch-map-hero__aura"/><span className="pitch-map-hero__pin"><Coffee /></span><span className="pitch-map-hero__label"><small>{hero.label}</small><strong>+{hero.points} pts</strong></span></div>}</div>;
}

function SlideEyebrow({ index }: { index: number }) {
  return <p className="pitch-number">{slideEyebrows[index]}</p>;
}

export function MerchantPitchPresentationPage() {
  const { isAdmin } = useAuth();
  const { locale, changeLocale } = usePublicLocale();
  const [storeName, setStoreName] = useState(() => isAdmin ? loadPresentationStoreName() : defaultPresentationStoreName);
  const [storeNameDraft, setStoreNameDraft] = useState(() => isAdmin ? loadPresentationStoreName() : defaultPresentationStoreName);
  const [configOpen, setConfigOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));
  const [mapPlaces, setMapPlaces] = useState<LandingPlace[]>(pitchMapFallbackPlaces);
  const [mapCity, setMapCity] = useState('เชียงใหม่');
  const [packages, setPackages] = useState<MerchantCampaignPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [averageSpend, setAverageSpend] = useState<number>(defaultAverageSpend);
  const deckRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectAverageSpend = (value: number) => {
    if (!Number.isFinite(value)) return;
    const bounded = Math.max(minimumAverageSpend, Math.min(maximumAverageSpend, Math.round(value)));
    setAverageSpend(bounded);
  };
  const goTo = useCallback((index: number) => {
    const bounded = Math.max(0, Math.min(slideTitles.length - 1, index));
    const deck = deckRef.current;
    const target = deck?.querySelector<HTMLElement>(`[data-slide="${bounded}"]`);
    if (!deck || !target) return;
    deck.scrollTo({ top: target.offsetTop, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const visibleStoreName = isAdmin ? loadPresentationStoreName() : defaultPresentationStoreName;
    setStoreName(visibleStoreName);
    setStoreNameDraft(visibleStoreName);
    if (!isAdmin) setConfigOpen(false);
  }, [isAdmin]);

  useEffect(() => {
    let current = true;
    setPackagesLoading(true);
    void apiRequest<{ items?: MerchantCampaignPackage[] }>('/merchant/campaign-packages').then((result) => {
      if (!current) return;
      setPackages((result.items || []).filter((item) => item.status !== 'stopped').sort((a, b) => a.price_amount - b.price_amount));
    }).catch(() => { if (current) setPackages([]); }).finally(() => { if (current) setPackagesLoading(false); });
    return () => { current = false; };
  }, []);

  useEffect(() => {
    let current = true;
    const location = pitchMapLocation();
    void loadLandingData(location.city, location.coords, locale).then((data) => {
      if (!current) return;
      const realPlaces = data.nearby_places.filter((place) => Boolean(place.place_id && place.name && place.lat !== null && place.lng !== null));
      if (realPlaces.length > 0) setMapPlaces(realPlaces.slice(0, 5));
      setMapCity(data.location.city_localized || data.location.city || location.city);
    }).catch(() => { /* Keep the explicit presentation fallback only when the live landing feed is unavailable. */ });
    return () => { current = false; };
  }, [locale]);

  useEffect(() => {
    const deck = deckRef.current;
    if (!deck) return;
    // A nested horizontal rail must never shift the presentation canvas.
    // Reset stale scrollLeft retained by hot reload and guard trackpad/touch
    // gestures so every slide keeps the same left/right safe area.
    const keepDeckOnVerticalAxis = () => {
      if (deck.scrollLeft !== 0) deck.scrollLeft = 0;
    };
    keepDeckOnVerticalAxis();
    deck.addEventListener('scroll', keepDeckOnVerticalAxis, { passive: true });
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio >= .55) setActive(Number((entry.target as HTMLElement).dataset.slide));
    }), { root: deck, threshold: [.55, .75] });
    deck.querySelectorAll('[data-slide]').forEach((slide) => observer.observe(slide));
    return () => {
      deck.removeEventListener('scroll', keepDeckOnVerticalAxis);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const onFullscreen = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLButtonElement) return;
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) { event.preventDefault(); goTo(active + 1); }
      if (['ArrowUp', 'PageUp'].includes(event.key)) { event.preventDefault(); goTo(active - 1); }
      if (event.key === 'Home') { event.preventDefault(); goTo(0); }
      if (event.key === 'End') { event.preventDefault(); goTo(slideTitles.length - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, goTo]);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) await rootRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };
  const saveStoreName = () => {
    if (!isAdmin) {
      setStoreName(defaultPresentationStoreName);
      setStoreNameDraft(defaultPresentationStoreName);
      return defaultPresentationStoreName;
    }
    const normalized = storeNameDraft.trim() || defaultPresentationStoreName;
    setStoreName(normalized);
    setStoreNameDraft(normalized);
    try { localStorage.setItem(presentationStoreNameKey, normalized); } catch { /* Keep the value for this session. */ }
    return normalized;
  };
  const startPresentation = async () => {
    saveStoreName();
    setConfigOpen(false);
    goTo(0);
    if (!document.fullscreenElement) await rootRef.current?.requestFullscreen();
  };
  const heroSource = mapPlaces.find((place) => place.has_campaign && place.campaign && place.campaign.reward_points > 0)
    || mapPlaces.find((place) => place.selected)
    || mapPlaces[0]
    || pitchMapFallbackPlaces[0];
  const presentationPlaces = [
    { ...heroSource, name: storeName, selected: true },
    ...mapPlaces.filter((place) => place.place_id !== heroSource.place_id).map((place) => ({ ...place, selected: false })),
  ];
  const mapHeroName = storeName;
  const mapHeroPoints = heroSource.campaign?.reward_points || 10;
  const entryPackage = packages.find((item) => item.package_id === 'trial_50') || packages[0];
  const spendProgress = spendPosition(averageSpend);
  const creatorRewards = packages.map((item) => Math.max(0, Number(item.creator_reward_per_review) || 0)).filter((value) => value > 0).sort((a, b) => a - b);
  const creatorRewardBenchmark = creatorRewards.length > 0 ? creatorRewards[Math.floor(creatorRewards.length / 2)] : 0;
  const downloadMarkdown = () => {
    const packageRows = packages.length > 0
      ? packages.map((item) => {
        const economics = packageEconomics(item, averageSpend);
        return `| ${packageObjectives[item.package_id] || 'เลือกให้เหมาะกับร้าน'} | ${presentationPackageName(item)} | ${economics.investment.toLocaleString('th-TH')} บาท | ${economics.reviewSlots.toLocaleString('th-TH')} | ${economics.estimatedRevenue.toLocaleString('th-TH')} บาท | ${economics.revenueMultiple.toLocaleString('th-TH', { maximumFractionDigits: 2 })}x | ลงทุน ${economics.investment.toLocaleString('th-TH')} บาท → ยอดขายประมาณการ ${economics.estimatedRevenue.toLocaleString('th-TH')} บาท |`;
      }).join('\n')
      : '| — | ยังไม่มีแพ็กเกจเปิดขาย | — | — | — | — | — |';
    const entryPrice = entryPackage ? `${Number(entryPackage.price_amount).toLocaleString('th-TH')} บาท` : 'แพ็กเกจเริ่มต้นที่กำลังเปิดขาย';
    const entryOffer = entryPackage
      ? `เริ่มแคมเปญแรก ${entryPrice} · รองรับรีวิวที่ผ่านเกณฑ์สูงสุด ${Number(entryPackage.review_slots).toLocaleString('en-US')} · ผู้รีวิวรับ +${Number(entryPackage.creator_reward_per_review).toLocaleString('en-US')} บาท · ยืนยันว่ามาถึงร้านจริง`
      : 'เลือกแพ็กเกจที่กำลังเปิดขาย แล้วเริ่มทดลองกับร้านคุณ';
    const markdown = presentationMarkdown.replaceAll('{{STORE_NAME}}', storeName).replaceAll('{{AVERAGE_SPEND}}', String(averageSpend)).replace('{{PACKAGE_ROWS}}', packageRows).replace('{{ENTRY_PRICE}}', entryPrice).replace('{{ENTRY_OFFER}}', entryOffer);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'beaw-merchant-presentation.md';
    document.body.appendChild(anchor);
    anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
  };

  return <div className="pitch-page" ref={rootRef}>
    <PublicHeader locale={locale} onLocaleChange={changeLocale} />
    <main className="pitch-stage">
      {isAdmin && configOpen ? <div className="pitch-present-config" role="dialog" aria-modal="true" aria-labelledby="pitch-present-config-title">
        <form className="pitch-present-config__panel" onSubmit={(event) => { event.preventDefault(); void startPresentation(); }}>
          <span className="pitch-present-config__icon"><Settings2 /></span>
          <div className="pitch-present-config__heading"><small>START PRESENT CONFIG</small><h2 id="pitch-present-config-title">ตั้งค่าก่อนเริ่มนำเสนอ</h2><p>ชื่อร้านนี้จะแสดงในแผนที่ ตัวอย่างแคมเปญ และสไลด์สรุปทั้งหมด</p></div>
          <label htmlFor="pitch-store-name">ชื่อร้าน</label>
          <input id="pitch-store-name" value={storeNameDraft} onChange={(event) => setStoreNameDraft(event.target.value)} placeholder={defaultPresentationStoreName} maxLength={80} autoFocus />
          <div className="pitch-present-config__memory"><Check /> ระบบจะจำชื่อร้านนี้ไว้สำหรับการนำเสนอครั้งถัดไป</div>
          <div className="pitch-present-config__actions"><button type="button" className="button" onClick={() => { saveStoreName(); setConfigOpen(false); }}>บันทึกและดูสไลด์</button><button type="submit" className="button button--orange"><Play /> เริ่ม Present</button></div>
        </form>
      </div> : null}
      <aside className="pitch-thumbnails" aria-label="เลือกสไลด์">
        {slideTitles.map((title, index) => <button key={title} className={active === index ? 'active' : ''} onClick={() => goTo(index)} aria-label={`สไลด์ ${index + 1}: ${title}`} aria-current={active === index ? 'step' : undefined}><span>{index + 1}</span><span><b>{title}</b><i>{slideTags[index]}</i></span></button>)}
      </aside>
      <div className="pitch-deck" ref={deckRef}>
        <section className="pitch-slide pitch-slide--quest" data-slide="0"><div className="pitch-slide__content">
          <SlideEyebrow index={0} />
          <h2>พาคนใหม่<br /><em>มาลองร้านคุณ
</em></h2><p className="pitch-quest-question">“วันนี้กินอะไร?” &nbsp; “จะไปร้านไหนดี?” &nbsp; “มีอะไรน่าลองใกล้ ๆ?”</p>
          <div className="pitch-map-layout"><QuestMap key={`choice-map-${active === 0 ? 'active' : 'idle'}`} places={presentationPlaces} city={mapCity} hero={{ label: mapHeroName, points: mapHeroPoints }} /><div className="pitch-map-copy"><Sparkles /><h3>เหมือน Quest บนแผนที่</h3><p>ร้านทั่วไปช่วยให้คนค้นพบ<br /><b>แคมเปญเพิ่มเหตุผลให้เลือก {mapHeroName}</b></p><div><span>● ร้านรอบตัว</span><span className="orange">● ร้านที่มีสิทธิพิเศษ</span></div></div></div>
          <p className="pitch-question">จากคนที่กำลังตัดสินใจ
<b></b> <ArrowDown /> <em>สู่คนที่เข้าร้านและทดลองใช้จริง</em></p>
        </div></section>

        <section className="pitch-slide pitch-slide--how" data-slide="1"><div className="pitch-slide__content">
          <SlideEyebrow index={1} />
          <header className="pitch-how-header pitch-how-header--visual"><h2>Beaw ทำงานง่าย ๆ</h2><div className="pitch-how-summary"><b>ร้านเปิด Campaign</b><ArrowRight /><b>ลูกค้ามาลองและรีวิว</b><ArrowRight /><b>ร้านได้คุณค่า</b></div></header>
          <div className="pitch-how-showcase">
            <article className="pitch-how-panel pitch-how-panel--merchant">
              <strong className="pitch-how-panel__label">01&nbsp;&nbsp; ร้าน</strong>
              <div className="pitch-how-panel__steps">
                <span><i><MapPin /></i><span><b>ปักหมุดร้าน</b><small>ให้ลูกค้าค้นพบร้านบน Beaw</small></span></span>
                <span><i><Gift /></i><span><b>เปิด Campaign</b><small>ใส่ Reward และเงื่อนไข<br />เพื่อดึงดูดลูกค้า</small></span></span>
                <span><i><Eye /></i><span><b>ติดตามผล</b><small>ดูคนเข้ามาลอง ใช้บริการ<br />และรีวิว</small></span></span>
              </div>
            </article>
            <span className="pitch-how-showcase__bridge" aria-hidden="true"><ArrowRight /></span>
            <article className="pitch-how-panel pitch-how-panel--customer">
              <strong className="pitch-how-panel__label">02&nbsp;&nbsp; ลูกค้า</strong>
              <div className="pitch-how-panel__steps">
                <span><i><Search /></i><span><b>ค้นหาร้าน ค้นหารีวิว</b><small>ค้นหาร้านใกล้ตัว<br />และดูรีวิวจากคนอื่น</small></span></span>
                <span><i><Store /></i><span><b>ใช้บริการ</b><small>เลือกไปร้านที่น่าสนใจ<br />มาลองใช้บริการ</small></span></span>
                <span><i><PenLine /></i><span><b>รีวิวประสบการณ์</b><small>แชร์ประสบการณ์หลังใช้บริการ</small></span></span>
                <span><i><Gift /></i><span><b>รับแต้ม</b><small>รับแต้มเป็นรางวัล</small></span></span>
              </div>
            </article>
          </div>
          <section className="pitch-how-outcomes"><strong className="pitch-how-outcomes__label">03&nbsp;&nbsp; ร้านได้อะไร</strong><div><span><Check /><span><b>ลูกค้ามาใช้จริง</b><small>คนเข้ามาลองใช้บริการจริง</small></span></span><span><Check /><span><b>รีวิวจากคนที่มาร้านจริง</b><small>รีวิวจากประสบการณ์จริง</small></span></span><span><Check /><span><b>Insight คนพูดถึงร้านอย่างไร</b><small>รู้ว่าลูกค้าพูดถึงร้านคุณอย่างไร</small></span></span></div></section>
        </div></section>

        <section className="pitch-slide pitch-slide--trust" data-slide="2"><div className="pitch-slide__content">
          <SlideEyebrow index={2} /><h2>ไปจริง รีวิวจริง<br /><em>ถึงได้ Reward</em></h2>
          <div className="pitch-trust-infographic">
            <div className="pitch-trust-radius-map"><span className="pitch-trust-radius-map__100">100 ม.</span><span className="pitch-trust-radius-map__50">50 ม.</span><div className="pitch-trust-store"><Store /><b>{storeName}</b></div><MapPin className="pitch-trust-radius-map__pin" /></div>
            <div className="pitch-trust-system">
              <div className="pitch-trust-path"><div><span><MapPin /></span><b>อยู่ในรัศมีร้าน</b><small>50–100 เมตร</small></div><ArrowRight /><div><span><Camera /></span><b>On-site รีวิว + รูปถ่าย</b><small>ประสบการณ์จริง</small></div><ArrowRight /><div className="is-reward"><span><Gift /></span><b>Admin</b><small>ตรวจสอบ</small></div><ArrowRight /><div><span><BadgeCheck /></span><b>รีวิวผ่าน</b><small>เผยแพร่</small></div></div>
              <div className="pitch-trust-rider-proof"><span><FileText /></span><div><small>อีกวิธียืนยัน</small><b>สั่งผ่าน Rider ก็รีวิวได้</b><p>แนบหลักฐาน เช่น บิลหรือใบเสร็จ เพื่อยืนยันว่าใช้บริการจริง</p></div><em>ใช้ตรวจสอบเท่านั้น</em></div>
              <div className="pitch-trust-divider"><span>ความน่าเชื่อถือตรวจสอบได้ · ยืนยันด้วย Location หรือหลักฐาน</span></div>
              <div className="pitch-trust-pillars"><article><MapPin /><div><b>ใช้บริการจริง</b><span>พิกัด หรือหลักฐาน</span></div></article><article><Star /><div><b>รีวิวอิสระ</b><span>จากประสบการณ์จริง</span></div></article><article><ShieldCheck /><div><b>ตรวจสอบได้</b><span>ระบบตรวจสอบหลายชั้น</span></div></article></div>
            </div>
          </div>
          <div className="pitch-no-buy"><b>ตรวจสอบการมาร้านได้</b><b>เห็นผลลัพธ์จากรีวิวจริง
</b></div>
        </div></section>

        <section className="pitch-slide pitch-slide--insight" data-slide="3"><div className="pitch-slide__content">
          <SlideEyebrow index={3} />
          <header className="pitch-insight-header"><div><h2>ร้านไม่ได้แค่จำนวนรีวิว</h2><p>แต่ได้รู้ว่า <b>“คนพูดถึงร้านคุณว่าอะไร”</b></p></div><span>ตัวอย่าง Community Insight</span></header>
          <div className="pitch-insight-metrics"><div><PenLine /><span><strong>128</strong> รีวิวผ่านเกณฑ์</span></div><div><Users /><span><strong>98</strong> Creators</span></div><div><Eye /><span><strong>12,450</strong> Views</span></div></div>
          <div className="pitch-insight-body">
            <section><h3>คนพูดถึงอะไรมากที่สุด</h3><div className="pitch-insight-topics"><span>ลาเต้ <b>82</b></span><span>ไม่หวาน <b>46</b></span><span>นั่งทำงาน <b>31</b></span><span>คั่วกลาง <b>27</b></span></div><h3>สิ่งที่คนชอบ</h3><ul><li><Check />ลาเต้หวานน้อย</li><li><Check />มีเมล็ดให้เลือก</li><li><Check />ร้านเงียบ นั่งทำงานได้</li></ul></section>
            <section className="pitch-insight-voices"><h3><MessageCircle /> เสียงจากรีวิวจริง</h3><blockquote>“ลาเต้ไม่หวาน คั่วกลางกำลังดี”</blockquote><blockquote>“ร้านเงียบ นั่งทำงานได้นาน”</blockquote><blockquote>“มีเมล็ดกาแฟให้เลือกหลายแบบ”</blockquote></section>
          </div>
          <div className="pitch-insight-meaning"><Brain /><div><small>BEAW INSIGHT · WHAT IT MEANS</small><span>ลูกค้าจำร้านคุณว่า</span><strong>“ร้านกาแฟที่ลาเต้ไม่หวาน นั่งทำงานได้ และมีเมล็ดให้เลือก”</strong></div></div>
          <div className="pitch-insight-uses"><span>นำไปใช้ต่อได้</span>{['MENU', 'CONTENT', 'POSITIONING', 'CAMPAIGN', 'SERVICE'].map((item) => <b key={item}>{item}</b>)}</div>
        </div></section>

        <section className="pitch-slide pitch-slide--choice pitch-slide--roi" data-slide="4"><div className="pitch-slide__content">
          <SlideEyebrow index={4} /><header className="pitch-roi-header"><h2>ลงทุนกับ Beaw Campaign<br /><em>แล้วได้อะไรกลับมา?</em></h2></header>
          <div className="pitch-spend-scale"><div className="pitch-spend-scale__value"><span>ยอดใช้จ่ายเฉลี่ยต่อคน · ช่วง {minimumAverageSpend}–{maximumAverageSpend} บาท</span><strong aria-live="polite">{averageSpend.toLocaleString('th-TH')} บาท</strong></div><div className="pitch-spend-scale__range" style={{ '--spend-progress': `${spendProgress}%` } as CSSProperties}><output aria-live="polite">{averageSpend.toLocaleString('th-TH')} บาท</output><input type="range" min={minimumAverageSpend} max={maximumAverageSpend} step="1" value={averageSpend} onInput={(event) => selectAverageSpend(Number(event.currentTarget.value))} onChange={(event) => selectAverageSpend(Number(event.currentTarget.value))} aria-label="เลือกยอดใช้จ่ายเฉลี่ยต่อคน" aria-valuemin={minimumAverageSpend} aria-valuemax={maximumAverageSpend} aria-valuenow={averageSpend} aria-valuetext={`${averageSpend} บาท`} /><div className="pitch-spend-scale__markers" aria-label="ตัวอย่างยอดใช้จ่ายบนช่วง 50 ถึง 500 บาท">{assumedSpendOptions.map((option, index) => <button type="button" key={option.amount} className={`${option.amount === averageSpend ? 'active' : ''}${index === 1 ? ' is-staggered' : ''}`} style={{ '--marker-position': `${spendPosition(option.amount)}%` } as CSSProperties} onClick={() => selectAverageSpend(option.amount)}><i aria-hidden="true" /><span><b>{option.amount} บาท</b><small>{option.label}</small></span></button>)}</div></div></div>
          <div className="pitch-packages--live pitch-roi-grid" role="region" aria-label="ผลตอบแทนโดยประมาณของแพ็กเกจ Campaign" aria-live="polite" tabIndex={0}>{packagesLoading ? <p className="pitch-packages-state">กำลังคำนวณจากแพ็กเกจที่เปิดขาย…</p> : packages.length > 0 ? packages.map((item) => <CampaignRoiCard key={item.package_id} value={item} averageSpend={averageSpend} creatorRewardBenchmark={creatorRewardBenchmark} />) : <p className="pitch-packages-state">ขณะนี้ยังไม่มีแพ็กเกจที่เปิดขาย</p>}</div>
          {packages.length > 3 && <p className="pitch-package-scroll-hint">เลื่อนซ้าย–ขวาเพื่อดูแพ็กเกจทั้งหมด →</p>}
          <p className="pitch-roi-note">สูตร: จำนวนรีวิวสูงสุด × {averageSpend} บาท ÷ ราคาแพ็กเกจ · ประมาณการจากจำนวนสิทธิ์สูงสุดของแพ็กเกจ ไม่ใช่การรับประกันยอดขาย</p>
        </div></section>

        <section className="pitch-slide pitch-slide--closing" data-slide="5"><div className="pitch-slide__content">
          <div className="pitch-closing-layout"><section className="pitch-closing-copy"><SlideEyebrow index={5} /><h2>ให้คนรู้จัก <br /> {storeName}</h2><p>ลูกค้ามาร้านจริง<br /><em>ผ่านประสบการณ์จริง </em></p><div className="pitch-closing-benefits"><span><Users /><b>เข้าถึงลูกค้าใหม่</b><small>ที่กำลังมองหา</small></span><span><Store /><b>พาเข้าร้านจริง</b><small>เกิดประสบการณ์จริง</small></span><span><Banknote /><b>มีโอกาสซื้อซ้ำ</b><small>และบอกต่อ</small></span></div></section><QuestMap places={presentationPlaces} city={mapCity} hero={{ label: storeName, points: 7 }} /><aside className="pitch-closing-offer"><h3>สร้าง Campaign <br /> รับรีวิวจากลูกค้าที่มาร้านจริง</h3>{entryPackage ? <><span>ลูกค้าเข้าใช้ {Number(entryPackage.review_slots).toLocaleString('th-TH')} รีวิว</span><strong>{Number(entryPackage.price_amount).toLocaleString('th-TH')} <small>บาท</small></strong><button className="button button--orange">เปิด Campaign แรก <ArrowRight /></button></> : <p>{packagesLoading ? 'กำลังโหลดแพ็กเกจที่เปิดขาย…' : 'ขณะนี้ยังไม่มีแพ็กเกจที่เปิดขาย'}</p>}</aside></div>
        </div></section>

        <div className="pitch-footer-snap"><PublicFooter /></div>
      </div>
      {isAdmin ? <div className="pitch-export"><button onClick={() => { setStoreNameDraft(storeName); setConfigOpen(true); }}><Settings2 /><span>ร้าน</span></button><button onClick={downloadMarkdown}><FileText /><span>MD</span></button></div> : null}
      <div className="pitch-controls"><button onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="สไลด์ก่อนหน้า"><ChevronUp /></button><span><b>{String(active + 1).padStart(2, '0')}</b> / {String(slideTitles.length).padStart(2, '0')}</span><button onClick={() => goTo(active + 1)} disabled={active === slideTitles.length - 1} aria-label="สไลด์ถัดไป"><ChevronDown /></button><button onClick={toggleFullscreen} aria-label={fullscreen ? 'ออกจากโหมดเต็มจอ' : 'เปิดเต็มจอ'}>{fullscreen ? <Minimize /> : <Expand />}</button></div>
    </main>
  </div>;
}
